import wrapIndex from "../../00-base/global/utils/wrap-index";
import {
  waitForTimeout,
  waitForAnimationFrame,
} from "../../00-base/global/utils/wait-for-timeout";
import { supportsAnimationQuery } from "../../00-base/global/utils/media";
import { wideNavQuery } from "../../00-base/global/utils/media";
import { keys } from "../../00-base/global/utils/keycodes";

const SUBMENU_CLASS= "header__submenu-cont";
const SUBMENU_SELECTOR = `.${SUBMENU_CLASS}`;
const SUBMENU_IN_CLS = "menu-in";
const SUBMENU_OUT_CLS = "menu-out";
const SUBMENU_TRANSITION_CLASSES = [SUBMENU_IN_CLS, SUBMENU_OUT_CLS];

const EXPANDED_ATTR = 'aria-expanded';

function expandButton(btn) {
  btn.setAttribute(EXPANDED_ATTR, 'true');
}


function collapseButton(buttonEl) {
  buttonEl.setAttribute(EXPANDED_ATTR, "false");
}

function getFirstMenuItem(el) {
  return el.querySelector(".js-menu-item");
}

 class DisclosureMenuItem {

  expanderBtn;
  submenuCont;

  containerEl;

  subLinks;
  beforeOpen;
  afterOpen;
  afterClose;

  index;

  constructor(
    listItemEl,
    index,
    beforeOpenFunc,
    afterOpenFunc,
    afterCloseFunc
  ) {

    this.containerEl = listItemEl;
    const expanderSourceEl = getFirstMenuItem(listItemEl);
    if (expanderSourceEl.tagName.toUpperCase() == 'BUTTON') {
      this.expanderBtn = expanderSourceEl;
    } else {
      this.expanderBtn = DisclosureMenuItem.replaceWithButton(expanderSourceEl);
    }
    
    this.index = index;
    this.expanderBtn.dataset.index = index;

    this.submenuCont = listItemEl.querySelector(SUBMENU_SELECTOR);
    this.subLinks = Array.from(this.submenuCont.querySelectorAll("a"));

    this.subLinks.forEach((lnk, i) => {
      lnk.dataset.parentIndex = index;
      lnk.dataset.index = i;
    });

    this.beforeOpen = beforeOpenFunc ?? (() => {});
    this.afterOpen = afterOpenFunc ?? (() => {});
    this.afterClose = afterCloseFunc ?? (() => {});

    this.setExpanderButtonListener();
    this.setTransitionListener();
  }

  static replaceWithButton(sourceEl) {

    const buttonEl = document.createElement('button');
    Array.from(sourceEl.childNodes).forEach((n) => {
      buttonEl.appendChild(n);
    })
 
    buttonEl.setAttribute('class', sourceEl.className);
    collapseButton(buttonEl);

    sourceEl.after(buttonEl);
    sourceEl.remove();
    return buttonEl;
  }

  openNoFocus() {
    this.open();
    this.afterOpen(this.index);
  }

  open() {
    this.beforeOpen();
    expandButton(this.expanderBtn);
    if (supportsAnimationQuery.matches) {
      this.submenuCont.classList.add(SUBMENU_IN_CLS);
    }
  }

  async openWithFocus(focusIndex = 0) {
    this.open();
    await waitForTimeout(150);
    this.focusSublink(focusIndex);
    await waitForAnimationFrame();
    this.afterOpen(this.index);
  }

  close() {
    collapseButton(this.expanderBtn);

    if (supportsAnimationQuery.matches) {
      this.submenuCont.classList.add(SUBMENU_OUT_CLS);
    }
    this.afterClose();
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.openNoFocus();
    }
  }

  isOpen() {
    return this.expanderBtn.getAttribute(EXPANDED_ATTR) == "true";
  }

  contains(el) {
    return this.containerEl.contains(el);
  }

  clearTransitions() {
    SUBMENU_TRANSITION_CLASSES.forEach((cls) => {
      this.submenuCont.classList.remove(cls);
    });
  }

  setExpanderButtonListener() {
    const self = this;
    this.expanderBtn.addEventListener("click", (e) => {
      e.preventDefault();
      self.toggle();
    });
  }

  setTransitionListener() {
    const transitionEndHandler = (e) => this.clearTransitions();
    this.submenuCont.addEventListener("animationend", transitionEndHandler);
  }


  addListeners() {
    this.setExpanderButtonListener();
    this.setTransitionListener();
  }

  focusExpander() {
    this.expanderBtn.focus();
  }

  focusSublink(index) {
    const normalizedIndex = index >= 0 ? index : this.subLinks.length + index;
    this.subLinks[normalizedIndex].focus();
  }

  focusSiblingEl(srcEl, offset) {
    const index = parseInt(srcEl.dataset.index);
    const focusIndex = wrapIndex(this.subLinks, index + offset);
    this.focusSublink(focusIndex, true)
  }

  focusDefault() {
    this.focusFirst();
  }

  focusFirst() {
    this.focusSublink(0);
  }

  focusLast() {
    this.focusSublink(this.subLinks.length - 1);
  }
}

class DisclosureMenu {
  parentElement = null;
  parentMenus = {};
  currentOpen = null;

  constructor(parentEl) {
    this.parentElement = parentEl;

    const listItems = Array.from(
      parentEl.querySelectorAll(".js-header__main-menu > li")
    );

    listItems.forEach((listItemEl, index) => {
      const menuItem = new DisclosureMenuItem(
        listItemEl,
        index,
        () => this.close(),
        (newIndex) => this.updateCurrent(newIndex),
        () => this.clearCurrent()
      );
      this.parentMenus[menuItem.index] = menuItem;
    });

    this.addKeyboardListeners();
    this.addClickOutListener();  
    
  }

  close() {
    if (this.currentOpen) {
      this.currentOpen.close();
    }
  }

  clearCurrent() {
    this.currentOpen = null;
  }

  updateCurrent(newIndex) {
    this.currentOpen = this.parentMenus[newIndex];
  }


  addKeyboardListeners() {
    const keyPressFunc = (e) => this.onKeyPress(e);
    this.parentElement.addEventListener("keydown", keyPressFunc);
  }

  onKeyPress(e) {
    if (this.handleCustomShortcut(e)) {
      e.preventDefault();
    }
  }

  openSiblingNav(offset, el) {
    const curIndex = el.dataset.parentIndex ?? el.dataset.index ?? "0";
    const navIndex = wrapIndex(
      Object.keys(this.parentMenus),
      parseInt(curIndex) + offset
    );
    if (this.currentOpen) {
      this.parentMenus[navIndex].openWithFocus(0);
    } else {
      this.parentMenus[navIndex].focusExpander();
    }
  }

  moveWithinNav(offset, el) {
    if (el.dataset.parentIndex) {
      this.parentMenus[el.dataset.parentIndex].focusSiblingEl(el, offset);
    } else {
      if (!this.parentMenus[el.dataset.index].isOpen()) {
        const focusIndex = offset > 0 ? 0 : -1;
        this.parentMenus[el.dataset.index].openWithFocus(focusIndex);
      } else {
        focusOffset();
      }
    }

    function focusOffset() {
      if (offset > 0) {
        this.parentMenus[el.dataset.index].focusFirst();
      } else {
        this.parentMenus[el.dataset.index].focusLast();
      }
    }
  }

  handleCustomShortcut(e) {
    switch (e.keyCode) {
      case keys.esc:
        if (this.currentOpen) {
          const expandedItem = this.currentOpen;
          this.close();
          expandedItem.focusExpander();
        } else {
          return false;
        }
        break;

      case keys.right:
        // refocus  on pressing right arrow, if last go to first
        this.openSiblingNav(1, e.target);
        break;
      case keys.left:
        // refocus on pressing left arrow, if first go to last
        this.openSiblingNav(-1, e.target);
        break;

      // open + refocus on pressing down arrow, if last go to first
      case keys.down:
        this.moveWithinNav(1, e.target);
        break;
      // open + refocus on pressing up arrow, if first go to last
      case keys.up:
        this.moveWithinNav(-1, e.target);
        break;
      default:
        return false;
    }
    return true;
  }

  addClickOutListener() {
    const clickOutHandler = (e) => this.closeOnClickOut(e);
    document.body.addEventListener("mousedown", clickOutHandler, true);
  }

  closeOnClickOut(e) {
    if (!this.parentElement.contains(e.target)) {
      this.close();
    }
  }
}

const DIALOG_IN_CLS = 'dialog-in';
 const DIALOG_OUT_CLS = 'dialog-out';

 const DIALOG_TRANSITION_CLASSES=[DIALOG_IN_CLS,DIALOG_OUT_CLS]; 

class MenuDialog {
  dialogEl;
  openBtn;

  constructor(dialogEl, openBtn, siteNav) {
    this.dialogEl = dialogEl;
    this.openBtn = openBtn;

    const narrowNavMenu = new DisclosureMenu(
     siteNav
    );

    getFirstMenuItem(siteNav).setAttribute('autofocus', 'autofocus');
    const closeButton = this.dialogEl.querySelector(
      ".header__mobile-btn"
    );

    const openHandler = (e) => this.open(e);
    this.openBtn.addEventListener("click", openHandler);

    const closeHandler = (e) => this.close(e);
    closeButton.addEventListener("click", closeHandler);

    const closeDialogHandler = (e) => this.cleanUpDialogClose(e);


    this.dialogEl.addEventListener("close", closeDialogHandler);
    this.dialogEl.addEventListener("keyup", (e) => {
      if (e.key === keys.esc) {
        this.close();
      }
    });

    this.setTransitionListener();

  }

  isOpen() {
    return this.dialogEl.open;
  }

  open() {
    this.dialogEl.showModal();
    if (supportsAnimationQuery.matches) {
      this.dialogEl.classList.add(DIALOG_IN_CLS);
    }
  }

  cleanUpDialogClose() {
    this.openBtn.focus();
    if (supportsAnimationQuery.matches) {
      this.dialogEl.classList.add(DIALOG_OUT_CLS);
    }
  }

  close() {
    this.dialogEl.close();

  }

  clearTransitions() {
    DIALOG_TRANSITION_CLASSES.forEach((cls) => {
      this.dialogEl.classList.remove(cls);
    });
  }

  handleTransition(e) {
    if (e.animationName == "dialogin" || e.animationName == "dialogin-banner") {
      this.clearTransitions();
    }
  }
  setTransitionListener() {
    const transitionHandler = (e) => this.handleTransition(e);
    this.dialogEl.addEventListener("animationend", transitionHandler);
  }
}

/**
 * Initialises the navbar functionality.
 * @return {[undefined]}
 */
export default function () {
  const wrapperEl = document.querySelector(".head-wrapper");

  let menuDialog = null;
  let wideNav = null;

  initNav(wideNavQuery);

  wideNavQuery.addEventListener('change', initNav);

  wrapperEl.classList.add("js-attached");

  function initNav(matcheable) {
     if (matcheable.matches && !wideNav) {
       wideNav = initWideNav();
     } else if (!matcheable.matches && !menuDialog) {
       menuDialog = initMenuDialog();
     }
  }

  function initWideNav() {
    const menuStructureWide = new DisclosureMenu(
      wrapperEl.querySelector(".header__nav")
    );
    return menuStructureWide;
  }

  function initMenuDialog() {

    const clonedNavs = ["site-navigation", "quick-links"].map((id) =>
      document.getElementById(id).cloneNode(true)
    );

    const submenuContainers = Array.from(
      clonedNavs[0].querySelectorAll(SUBMENU_SELECTOR)
    );

    submenuContainers.forEach((cont, i) => {
      const children = Array.from(cont.children);
      children.forEach((childEl) => {
        if (!childEl.classList.contains("submenu-img")) {
          if (childEl.classList.contains("submenu-heading")) {
            childEl.setAttribute("class", "visually-hidden");
          } else if (childEl.classList.contains("header__submenu")) {
            childEl.classList.add(SUBMENU_CLASS, "light");
          }
          cont.parentElement.appendChild(childEl);
        }
      });
      cont.remove();
    });

    clonedNavs[1].removeAttribute("data-height-var-name");
 
    const dl = document.getElementById("narrow-nav-container");
    clonedNavs.forEach((nav) => {
      ['id', 'aria-labelledby'].forEach((attr) => updateUniqueAttr(nav, attr));
      dl.appendChild(nav);

    });

    menuDialog = new MenuDialog(
      dl,
      document.getElementById("btn-open-nav"),
      clonedNavs[0]
    );

    let bodyWidth = -1;
    const innerWidthObserver = new ResizeObserver(observeBodyWidth);
    innerWidthObserver.observe(document.body);


    function updateUniqueAttr(parent, attr) {
      const selfItem = parent.getAttribute(attr) ? [parent] : [];
      const items = [...selfItem, ...Array.from(parent.querySelectorAll(`*[${attr}]`))];
      items.forEach((item) => {
        item.setAttribute(attr, `${item.getAttribute(attr)}-dl`);
      });
    }

    function observeBodyWidth(entries) {
      if (!wideNavQuery.matches && !(menuDialog && menuDialog.isOpen())) {
        entries.forEach((entry) => {
          const resizedWidth = entry.borderBoxSize
            ? entry.borderBoxSize[0].inlineSize
            : entry.contentRect.width;
          updateBodyWidth(resizedWidth);
        });
      }
    }

    function updateBodyWidth(resizedWidth) {
      if (resizedWidth !== bodyWidth) {
        bodyWidth = resizedWidth;
        document.documentElement.style.setProperty(
          "--non-dialog-max-width",
          `${bodyWidth}px`
        );
      }
    }

    return menuDialog;

  }
}
